import React from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { InternalMessage, TimelineVariant } from '../../MessagesV2.types';
import { FAIL_STATUSES, RAW_STATUS_CONFIG } from '../../MessagesV2.constants';

// Derived from the generated GraphQL selection so it stays in sync if the query changes.
type TimelineEntry = InternalMessage['rawStatusHistory'][number];

type TimelineHistoryProps = Readonly<{
  entries: ReadonlyArray<TimelineEntry>;
  variant: TimelineVariant;
}>;

const RED = 'rgb(220,38,38)';
const GREEN = 'rgb(23,111,105)';
const INDIGO = '#4951EF';

// Full localized date + time + timezone — status transitions can span multiple days,
// so the date matters for debugging. Uses the browser locale to match the table-row
// timestamps (see MessagesTable createAt formatting).
function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

const labelFor = (rawStatus: string): string => RAW_STATUS_CONFIG[rawStatus]?.label || rawStatus;

// Circle color reflects the status tone, not just pass/fail: failures are red, and
// neutral/warning statuses (canceled, unsubscribed, …) keep their configured color
// instead of being shown as a green success.
const colorFor = (rawStatus: string): string => (FAIL_STATUSES.has(rawStatus) ? RED : RAW_STATUS_CONFIG[rawStatus]?.fg || GREEN);

// Sorted ascending (chronological) copy of the raw history.
const chronological = (entries: ReadonlyArray<TimelineEntry>): TimelineEntry[] => [...entries].sort((a, b) => a.date.milliseconds - b.date.milliseconds);

type ErrorBoxProps = Readonly<{ message: string; fontSize: string }>;

const ErrorBox: React.FC<ErrorBoxProps> = ({ message, fontSize }) => (
  <Box
    fontFamily="monospace"
    fontSize={fontSize}
    padding="6px 8px"
    borderRadius={4}
    style={{
      background: 'rgb(255,241,244)',
      border: '1px solid rgb(254,202,210)',
      color: '#7F1D1D',
      wordBreak: 'break-word',
    }}
  >
    {message}
  </Box>
);

type CircleProps = Readonly<{ color: string; glyph: string; size: number }>;

const Circle: React.FC<CircleProps> = ({ color, glyph, size }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    borderRadius="100px"
    fontSize="11px"
    fontWeight={700}
    style={{
      width: size,
      height: size,
      minWidth: size,
      background: color,
      color: '#FFFFFF',
    }}
  >
    {glyph}
  </Box>
);

const ListVariant: React.FC<{ entries: TimelineEntry[] }> = ({ entries }) => (
  <Box
    display="flex"
    flexDirection="column"
  >
    {entries.map((entry, i) => {
      const isFail = FAIL_STATUSES.has(entry.rawStatus);
      const isLast = i === entries.length - 1;
      return (
        <Box
          key={`${entry.rawStatus}-${entry.date.milliseconds}-${i}`}
          display="flex"
          gap="10px"
        >
          {/* Icon column with connector */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Circle
              color={colorFor(entry.rawStatus)}
              glyph={isFail ? '✗' : '✓'}
              size={20}
            />
            {!isLast && (
              <Box
                flex="1"
                marginY="2px"
                style={{ width: 2, minHeight: 12, background: '#E0E0E0' }}
              />
            )}
          </Box>
          {/* Content column */}
          <Box
            display="flex"
            flexDirection="column"
            gap="3px"
            paddingBottom={isLast ? '0' : '12px'}
            style={{ minWidth: 0, flex: 1 }}
          >
            <Box
              display="flex"
              alignItems="baseline"
              gap="8px"
              flexWrap="wrap"
            >
              <TextV2
                typographyVariant="label3"
                fontSize="12.5px"
                fontWeight={700}
              >
                {labelFor(entry.rawStatus)}
              </TextV2>
              <TextV2
                typographyVariant="label3"
                fontSize="11px"
                color="mono10"
              >
                {fmtDateTime(entry.date.milliseconds)}
              </TextV2>
            </Box>
            {entry.statusMessage && (
              <ErrorBox
                message={entry.statusMessage}
                fontSize="11px"
              />
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const StepperVariant: React.FC<{ entries: TimelineEntry[] }> = ({ entries }) => {
  // Index of the last non-failure step — rendered indigo to mark the current/active state.
  let lastNonFailIdx = -1;
  entries.forEach((entry, i) => {
    if (!FAIL_STATUSES.has(entry.rawStatus)) lastNonFailIdx = i;
  });

  return (
    <Box
      overflowX="auto"
      display="flex"
      alignItems="flex-start"
      gap="0"
      paddingBottom="6px"
    >
      {entries.map((entry, i) => {
        const isFail = FAIL_STATUSES.has(entry.rawStatus);
        const isLast = i === entries.length - 1;
        const circleColor = isFail ? RED : i === lastNonFailIdx ? INDIGO : colorFor(entry.rawStatus);
        return (
          <Box
            key={`${entry.rawStatus}-${entry.date.milliseconds}-${i}`}
            display="flex"
            alignItems="flex-start"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="4px"
              style={{ width: 120, minWidth: 120 }}
            >
              <Circle
                color={circleColor}
                glyph={isFail ? '✗' : '✓'}
                size={22}
              />
              <TextV2
                typographyVariant="label3"
                fontSize="12px"
                fontWeight={700}
                textAlign="center"
              >
                {labelFor(entry.rawStatus)}
              </TextV2>
              <TextV2
                typographyVariant="label3"
                fontSize="10.5px"
                color="mono10"
                textAlign="center"
              >
                {fmtDateTime(entry.date.milliseconds)}
              </TextV2>
              {entry.statusMessage && (
                <ErrorBox
                  message={entry.statusMessage}
                  fontSize="10px"
                />
              )}
            </Box>
            {!isLast && (
              <Box
                marginTop="10px"
                style={{ width: 36, height: 2, minWidth: 36, background: '#E0E0E0' }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const TimelineHistory: React.FC<TimelineHistoryProps> = ({ entries, variant }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const ordered = chronological(entries);
  if (ordered.length === 0) {
    return (
      <TextV2
        typographyVariant="label3"
        fontSize="12px"
        color="mono10"
      >
        {tq`No status history available`}
      </TextV2>
    );
  }
  return variant === 'stepper' ? <StepperVariant entries={ordered} /> : <ListVariant entries={ordered} />;
};
